"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Suspense, useEffect } from "react";

import { registerNavigate } from "../../../_lib/api";
import { useSession } from "../../../_lib/session";
import { PageTracker } from "../../../_lib/track";
import { useSite } from "../../../_lib/useSite";
import { BrandLogo } from "../../../_ui/BrandLogo";
import { Tooltip } from "../../../_ui/Tooltip";
import { Footer } from "./Footer";

/*
 * `simple` 스킨의 셸 — 얇은 흰 헤더 · 가운데 한 줄 · 법정 표시 푸터
 * (`docs/design/simple-skin/README.md` §5 레이아웃 규칙).
 *
 * 🔴 **하단 탭이 없습니다.** 탭은 갈 곳이 넷은 돼야 성립하는데 이 스킨은 구매 하나뿐입니다.
 *    «내 상품권» 은 헤더 메뉴로 들어갑니다(오너 결정).
 * 🔴 **PC 는 가운데 480 한 줄**입니다 — 모바일과 같은 카드를 폭만 두고 가운데로(README §5 «PC»).
 *    랜딩만 전체 폭(`width="full"`)입니다.
 * 🔴 **모든 simple 화면이 이것을 씁니다** — `default` 의 `AppShell` 과 같은 두 가지가 여기 있습니다:
 *    ① 접속 로그 신호(`PageTracker`) ② 401 을 만났을 때 로그인 화면으로 보낼 라우터 등록.
 * 🔑 헤더 높이 58/68 은 `--tnf-header-height`(`../tokens.css`).
 */
export function Shell({
  children,
  width = "column",
  top = "normal",
}: {
  children: React.ReactNode;
  /** `column` — 가운데 480 한 줄(기본) · `full` — 랜딩처럼 전체 폭(여백 없음) */
  width?: "column" | "full";
  /** 로그인·가입은 위 여백이 조금 넓습니다(시안 08·09 — 28) */
  top?: "normal" | "loose";
}) {
  const router = useRouter();

  useEffect(() => {
    /* 🔴 `replace` — 로그인 뒤 뒤로 가기가 만료된 화면으로 돌아가 다시 튕기지 않게(`default` 와 같음). */
    registerNavigate((href) => router.replace(href));
    return () => registerNavigate(null);
  }, [router]);

  return (
    <div className="flex min-h-dvh flex-col bg-ground">
      <Suspense fallback={null}>
        <PageTracker />
      </Suspense>
      <Header />
      <main className="flex w-full flex-1 flex-col">{width === "full" ? children : <Column top={top}>{children}</Column>}</main>
      <Footer />
    </div>
  );
}

/**
 * 가운데 480 한 줄 — 본문 좌우 16 · PC 위아래 52 (시안 01 · 14). 🔑 홈처럼 `width="full"` 인 화면이
 * 모바일 쪽 본문만 이 틀에 넣을 때 씁니다.
 */
export function Column({ children, top = "normal", className = "" }: { children: React.ReactNode; top?: "normal" | "loose"; className?: string }) {
  return (
    <div className={`w-full flex-1 px-4 pb-6 lg:px-10 lg:py-[52px] ${top === "loose" ? "pt-7" : "pt-5"} ${className}`}>
      <div className="mx-auto w-full max-w-[var(--tnf-panel-width)]">{children}</div>
    </div>
  );
}

function Header() {
  const { brand } = useSite();
  const session = useSession();
  const pathname = usePathname();
  const member = session.status === "member";

  /* 🔑 로그인 확인 전에는 자리만 잡습니다 — «로그인» 이 스쳤다가 «내 정보» 로 바뀌는 깜빡임을 막습니다. */
  const account = member
    ? { href: "/me", label: "내 정보", tooltip: "내 정보와 주문 내역을 봅니다." }
    : { href: "/login", label: "로그인", tooltip: "로그인 화면으로 갑니다." };

  return (
    <header className="sticky top-0 z-40 flex h-[var(--tnf-header-height)] flex-none items-center gap-3 border-b border-line bg-[var(--tnf-header)] px-[18px] lg:gap-3.5 lg:px-10">
      <Link href="/" aria-label={`${brand.siteName} 첫 화면`} className="flex min-w-0 items-center gap-2 text-ink lg:gap-[9px]">
        <BrandLogo
          className="grid size-[26px] flex-none place-items-center rounded-[var(--tnf-radius-xxs)] bg-ink text-white lg:size-[30px] lg:rounded-[var(--tnf-radius-xs)]"
          markClassName="size-3.5 lg:size-4"
          imageClassName="size-[26px] lg:size-[30px]"
        />
        <span className="truncate text-[length:calc(var(--tnf-text-base)*16/14)]/[1.5] font-bold tracking-[-0.02em] lg:text-[length:calc(var(--tnf-text-base)*18/14)]/[calc(1.75/1.125)]">{brand.siteName}</span>
      </Link>
      <nav aria-label="주 메뉴" className="ml-auto flex flex-none items-center gap-1 lg:gap-1.5">
        <HeaderLink href="/coupons" current={pathname.startsWith("/coupons")} tooltip="내가 산 상품권과 핀번호를 봅니다.">
          내 상품권
        </HeaderLink>
        <span className={session.status === "loading" ? "invisible" : ""}>
          <HeaderLink href={account.href} current={pathname.startsWith(account.href)} tooltip={account.tooltip} outlined={!member}>
            {account.label}
          </HeaderLink>
        </span>
      </nav>
    </header>
  );
}

function HeaderLink({ href, current, tooltip, outlined = false, children }: {
  href: string;
  current: boolean;
  tooltip: string;
  /** PC 의 «로그인» — 시안 14·15 는 테두리를 두릅니다 */
  outlined?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip text={tooltip} placement="bottom" align="right">
      {(trigger) => (
        <Link
          href={href}
          aria-current={current ? "page" : undefined}
          className={`rounded-[var(--tnf-radius-xs)] px-2.5 py-2 text-body font-medium whitespace-nowrap hover:bg-raised hover:text-ink lg:py-[9px] lg:text-lead ${
            current ? "text-ink" : "text-ink-soft"
          } ${outlined ? "lg:px-4 lg:font-bold lg:text-ink lg:shadow-[inset_0_0_0_1px_var(--tnf-line)]" : "lg:px-3.5"}`}
          {...trigger}
        >
          {children}
        </Link>
      )}
    </Tooltip>
  );
}
